import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject, throwError } from 'rxjs';
import {
  BooleanResponse,
  BusinessUser,
  Client,
  ClientPricesResponse,
  ClonePricesPayload,
  GetClientDetailsRequest,
  GetClientDetailsResponse,
  GetClientsResponse,
  PaginatedClientsResponse,
  PricesByZoneClientParams,
  UpdatePricesFeesByZoneClientRequest,
  UpdatePricesProductByPlanRequest,
  UpdatePricesProductsByZoneClientRequest,
  UpdateVerticalProductsPayload,
} from 'src/app/models/client/client.model';
import { environment } from 'src/environments/environment';
import { HttpErrorHandlerService } from '../http-error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private readonly url = environment.url;
  //url = 'http://localhost:3000';
  private clientCreatedSource = new Subject<void>();
  clientCreated$ = this.clientCreatedSource.asObservable();

  constructor(
    private http: HttpClient,
    private errorHandler: HttpErrorHandlerService
  ) {}

  notifyClientCreated(): void {
    this.clientCreatedSource.next();
  }

  getZones(): Observable<any> {
    return this.http.get(`${this.url}/zones`);
  }

  getClients(): Observable<BusinessUser[]> {
    return this.http
      .post<GetClientsResponse>(`${this.url}/clients/get`, {})
      .pipe(
        map((res: GetClientsResponse) => res.businessUserData),
        catchError(this.errorHandler.handleError)
      );
  }

  getPricesByZoneClient({
    zoneId,
    clientId,
    planType,
  }: PricesByZoneClientParams): Observable<ClientPricesResponse> {
    let params = new HttpParams()
      .set('zoneId', zoneId)
      .set('clientId', clientId);

    if (planType) params = params.set('planType', planType);

    return this.http
      .get<ClientPricesResponse>(
        `${this.url}/general_clients/get-prices-by-zone-client`,
        { params }
      )
      .pipe(
        catchError((err) => {
          console.error('[ClientService] getPricesByZoneClient →', err);
          return throwError(() => err);
        })
      );
  }

  updatePricesProductByPlan(
    body: UpdatePricesProductByPlanRequest
  ): Observable<ClientPricesResponse> {
    return this.http
      .post<ClientPricesResponse>(
        `${this.url}/general_clients/update-prices-product-by-plan`,
        body
      )
      .pipe(catchError((err) => this.errorHandler.handleError(err)));
  }

  updatePricesFeesByZoneClient(
    body: UpdatePricesFeesByZoneClientRequest
  ): Observable<BooleanResponse> {
    return this.http
      .post<BooleanResponse>(
        `${this.url}/general_clients/update-prices-fees-by-zone-client`,
        body
      )
      .pipe(catchError((err) => this.errorHandler.handleError(err)));
  }

  updatePricesProductsByZoneClient(
    body: UpdatePricesProductsByZoneClientRequest
  ): Observable<ClientPricesResponse> {
    return this.http
      .post<ClientPricesResponse>(
        `${this.url}/general_clients/update-prices-products-by-zone-client`,
        body
      )
      .pipe(catchError((err) => this.errorHandler.handleError(err)));
  }

  public updateVerticalProducts(
    body: UpdateVerticalProductsPayload
  ): Observable<BooleanResponse> {
    return this.http
      .post<void>(
        `${this.url}/general_clients/update-verticals-by-zone-client`,
        body
      )
      .pipe(
        map(() => ({ isSuccess: true })),
        catchError((_) => of({ isSuccess: false }))
      );
  }

  getPaginatedClients(
    pageNumber: number,
    pageSize = 10,
    zoneGuid = '',
    clientFilter = ''
  ): Observable<PaginatedClientsResponse> {
    const body = {
      pageNumber,
      pageSize,
      zoneGuid: zoneGuid ?? '',
      clientFilter: clientFilter ?? '',
    };

    return this.http
      .post<PaginatedClientsResponse>(`${this.url}/clients/paginated`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  getClientDetails(
    request: GetClientDetailsRequest
  ): Observable<GetClientDetailsResponse> {
    return this.http
      .post<GetClientDetailsResponse>(`${this.url}/clients/details`, request)
      .pipe(catchError(this.errorHandler.handleError));
  }

  clonePricesToZone(body: ClonePricesPayload): Observable<BooleanResponse> {
    return this.http
      .post<BooleanResponse>(
        `${this.url}/general_clients/clone-prices-to-zone`,
        body
      )
      .pipe(catchError((err) => this.errorHandler.handleError(err)));
  }

  /**
   * Fetch all clients (no pagination) with optional filters.
   * Calls backend POST /clients/get with body: { zoneGuid, clientFilter }
   */
  getAllClients(params?: {
    zoneGuid?: string;
    clientFilter?: string;
  }): Observable<BusinessUser[]> {
    const body = {
      zoneGuid: params?.zoneGuid ?? '',
      clientFilter: params?.clientFilter ?? '',
    };

    return this.http
      .post<GetClientsResponse>(`${this.url}/clients/get`, body)
      .pipe(
        map((res: GetClientsResponse) =>
          Array.isArray(res.businessUserData) ? res.businessUserData : []
        ),
        catchError((err) => {
          console.error('[ClientService] getAllClients error', err);
          return throwError(() => err);
        })
      );
  }
  
}
